/**
 * Utility functions for lenient numeric input sanitization and parsing.
 *
 * Supports international number formats (commas as decimal separators),
 * leading/trailing whitespace, thousands separators (spaces),
 * and leading decimals (e.g. ",5" -> "0.5").
 */

/**
 * Sanitizes a decimal input string on the fly:
 * - Replaces commas with dots
 * - Strips all internal and external whitespace
 * - Strips non-digit and non-dot characters
 * - Keeps only the first decimal dot
 * - Normalizes leading decimal dots (e.g. ".5" -> "0.5", "." -> "0.")
 *
 * @param {string|number|null|undefined} value
 * @returns {string} Cleaned decimal input string
 */
export function cleanDecimalInput(value) {
    if (value === null || value === undefined) {
        return "";
    }

    let str = String(value);

    // Replace all commas with dots
    str = str.replace(/,/g, ".");

    // Remove all whitespace (including non-breaking spaces)
    str = str.replace(/\s+/g, "");

    // Remove any character that is not a digit or dot
    str = str.replace(/[^0-9.]/g, "");

    // Only keep the first dot, remove subsequent dots
    const dotIndex = str.indexOf(".");
    if (dotIndex !== -1) {
        str = str.slice(0, dotIndex + 1) + str.slice(dotIndex + 1).replace(/\./g, "");
    }

    // Normalize leading decimal dot e.g. ".5" -> "0.5" or "." -> "0."
    if (str.startsWith(".")) {
        str = "0" + str;
    }

    return str;
}

/**
 * Sanitizes an integer input string on the fly (for reps):
 * - Strips all whitespace
 * - Strips non-digit characters (including dots and commas)
 *
 * @param {string|number|null|undefined} value
 * @returns {string} Cleaned integer string
 */
export function cleanIntegerInput(value) {
    if (value === null || value === undefined) {
        return "";
    }

    let str = String(value);

    // Remove all whitespace
    str = str.replace(/\s+/g, "");

    // Remove any non-digit character
    str = str.replace(/[^0-9]/g, "");

    return str;
}

/**
 * Parses a weight value into a non-negative float.
 * - Empty string or whitespace-only returns 0 (bodyweight / default)
 * - Strings with commas or spaces are cleaned and parsed
 * - Invalid, negative, or malformed inputs return null
 *
 * @param {string|number|null|undefined} value
 * @returns {number|null} Non-negative weight number, or null if invalid
 */
export function parseWeight(value) {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === "number") {
        return Number.isFinite(value) && value >= 0 ? value : null;
    }

    const str = String(value);
    if (str.trim() === "") {
        return 0;
    }

    if (str.trim().startsWith("-")) {
        return null;
    }

    const cleaned = cleanDecimalInput(str);
    if (cleaned === "" || cleaned === ".") {
        return null;
    }

    const num = parseFloat(cleaned);
    return Number.isFinite(num) && num >= 0 ? num : null;
}

/**
 * Parses repetitions into a strictly positive integer.
 * - Non-digits and decimals are stripped
 * - Invalid, non-positive, or empty inputs return null
 *
 * @param {string|number|null|undefined} value
 * @returns {number|null} Positive integer reps, or null if invalid
 */
export function parseReps(value) {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === "number") {
        return Number.isInteger(value) && value > 0 ? value : null;
    }

    const str = String(value).trim();
    if (!str || str.startsWith("-")) {
        return null;
    }

    const cleaned = cleanIntegerInput(str);
    if (!cleaned) {
        return null;
    }

    const num = parseInt(cleaned, 10);
    return Number.isInteger(num) && num > 0 ? num : null;
}

/**
 * Parses cardio duration into a strictly positive float (minutes).
 * - Strings with commas or spaces are cleaned and parsed
 * - Invalid, non-positive, or empty inputs return null
 *
 * @param {string|number|null|undefined} value
 * @returns {number|null} Positive cardio duration in minutes, or null if invalid
 */
export function parseCardioMinutes(value) {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === "number") {
        return Number.isFinite(value) && value > 0 ? value : null;
    }

    const str = String(value).trim();
    if (!str || str.startsWith("-")) {
        return null;
    }

    const cleaned = cleanDecimalInput(str);
    if (cleaned === "" || cleaned === ".") {
        return null;
    }

    const num = parseFloat(cleaned);
    return Number.isFinite(num) && num > 0 ? num : null;
}
